<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            badgeText="Huge upcoming feature"
            title="Automations for every important client touchpoint"
            subtitle="Automations help coaches, consultants, and solo experts run follow-ups around bookings, reminders, payments, and rebooking without manual busywork."
            buttonLabel="Join the early access list"
            :buttonUrl="heroRegisterUrl"
            subtitleMaxWidthClass="max-w-4xl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
                    <FeaturePageInfoCard
                        v-for="automationCapability in automationCapabilities"
                        :key="automationCapability.title"
                        :title="automationCapability.title"
                        :description="automationCapability.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-xl font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>

                <div class="mt-8 text-left sm:text-center">
                    <FeaturePagePrimaryButton
                        label="Join the early access list"
                        :href="ctaRegisterUrl"
                    />
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2">
                    <div>
                        <FeaturePageSectionHeader
                            title="Messaging controls and credit policy"
                            subtitle="SMS and WhatsApp actions include granular controls for message templates, merge tags, country targeting, and credit usage."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <div class="mt-5 grid gap-3 sm:grid-cols-2">
                            <FeaturePageInfoCard
                                v-for="messagingPolicy in messagingPolicies"
                                :key="messagingPolicy.title"
                                :title="messagingPolicy.title"
                                :description="messagingPolicy.description"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold text-slate-900"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </div>
                    </div>

                    <div>
                        <FeaturePageMockPanel
                            title="Automation logs"
                            containerClass="rounded-xl border border-slate-200 bg-slate-50 p-6"
                        >
                            <FeaturePageInfoCard
                                v-for="logExample in logExamples"
                                :key="logExample.title"
                                :title="logExample.title"
                                :description="logExample.description"
                                containerClass="rounded-xl border border-slate-200 bg-white p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Use cases you can ship with automations"
                    subtitle="Automations help solo experts stay responsive before and after the booking without relying on manual follow-up."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="useCase in useCases"
                        :key="useCase.title"
                        :title="useCase.title"
                        :description="useCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Action catalog"
                    subtitle="Actions can be chained in order, so one trigger can fan out to multiple channels and systems."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="action in actions"
                        :key="action.title"
                        :title="action.title"
                        :description="action.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-4"
                        titleClass="text-sm font-bold"
                        descriptionClass="mt-1 text-xs text-slate-600"
                    />
                </div>
            </div>
        </section>

        <section class="bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Trigger catalog"
                    subtitle="Start automations from booking, payment, attendance, cancellation, and client milestones."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="trigger in triggers"
                        :key="trigger.title"
                        :title="trigger.title"
                        :description="trigger.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                        titleClass="text-sm font-bold"
                        descriptionClass="mt-1 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface AutomationCapability {
        title: string
        description: string
    }

    interface MessagingPolicy {
        title: string
        description: string
    }

    interface Trigger {
        title: string
        description: string
    }

    interface Action {
        title: string
        description: string
    }

    interface LogExample {
        title: string
        description: string
    }

    interface UseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()
    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.AutomationsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.AutomationsCta,
    )

    const automationCapabilities: AutomationCapability[] = [
        {
            title: 'Unlimited automations',
            description:
                'Set up as many automations as needed for different offers, client segments, and workflows.',
        },
        {
            title: 'Enable or disable anytime',
            description:
                'Turn each automation on or off at any moment without deleting its configuration.',
        },
        {
            title: 'Multiple actions per trigger',
            description:
                'Each trigger can execute many actions in sequence, allowing one event to run a complete workflow.',
        },
        {
            title: 'Operational visibility',
            description:
                'Detailed logs show delivered, skipped, and failed actions so you can debug and improve each workflow quickly.',
        },
    ]

    const messagingPolicies: MessagingPolicy[] = [
        {
            title: 'Editable message content',
            description:
                'Edit SMS and WhatsApp templates directly inside each automation action.',
        },
        {
            title: 'Merge tags available',
            description:
                'Use dynamic values like client name, meeting date, and meeting time in reminder copy.',
        },
        {
            title: 'Monthly plan credits',
            description:
                'Every pricing plan includes reminder credits that reset every month.',
        },
        {
            title: 'Non-expiring add-on credits',
            description:
                'Purchased extra credits can be used anytime and do not expire.',
        },
        {
            title: 'Country-based sending rules',
            description:
                'Enable SMS and WhatsApp delivery only in selected countries to control variable costs.',
        },
    ]

    const triggers: Trigger[] = [
        {
            title: 'X minutes after meeting ends',
            description:
                'Run follow-ups a configurable number of minutes after completion.',
        },
        {
            title: 'New meeting is scheduled',
            description: 'Fire when a standard meeting is created.',
        },
        {
            title: 'New attendee is scheduled',
            description:
                'For group sessions, trigger only from the second attendee onward.',
        },
        {
            title: 'New client',
            description:
                'Run onboarding automations when a first client profile is created.',
        },
        {
            title: 'Meeting marked as no-show',
            description:
                'Available once the no-show meeting state is introduced.',
        },
        {
            title: 'Pending meeting confirmed',
            description:
                'Available once the pending meeting state is introduced.',
        },
        {
            title: 'New payment received',
            description:
                'Trigger revenue and fulfillment workflows immediately after payment.',
        },
        {
            title: 'Payment refunded',
            description:
                'Handle refund alerts, internal records, and client communication.',
        },
        {
            title: 'Attendee cancels spot',
            description:
                'For group meetings, fire when an attendee leaves without canceling the full session.',
        },
        {
            title: 'Meeting canceled',
            description:
                'Trigger cancellation workflows for one-off meetings and recurring meeting instances.',
        },
        {
            title: 'Attendee reschedules meeting',
            description: 'Run updates after date/time changes.',
        },
        {
            title: 'Right after meeting is scheduled',
            description:
                'Immediate trigger for instant confirmations and internal notifications.',
        },
        {
            title: 'Client last booked X time ago',
            description: 'Segment clients by inactivity windows.',
        },
        {
            title: 'Client booked X meetings',
            description:
                'Trigger milestone actions when total booking count reaches a threshold.',
        },
        {
            title: 'Client booked X meetings in last X time',
            description:
                'Detect high-frequency booking behavior in a rolling period.',
        },
        {
            title: 'Client booked first meeting',
            description:
                'Launch welcome and activation flows after first conversion.',
        },
    ]

    const actions: Action[] = [
        {
            title: 'Send email',
            description: 'Send transactional or lifecycle email content.',
        },
        {
            title: 'Send webhook',
            description: 'Push event payloads to external systems.',
        },
        {
            title: 'Send SMS',
            description: 'Deliver short reminders and updates by SMS.',
        },
        {
            title: 'Send WhatsApp message',
            description: 'Deliver reminder messages through WhatsApp.',
        },
        {
            title: 'Send Slack message',
            description:
                'Notify an internal Slack channel when an important event happens.',
        },
        {
            title: 'Send Telegram message',
            description:
                'Notify a Telegram chat when an important event happens.',
        },
    ]

    const logExamples: LogExample[] = [
        {
            title: 'Skipped by country rule',
            description:
                'Example: SMS action skipped because attendee country was not in the selected country list.',
        },
        {
            title: 'Skipped by trigger condition',
            description:
                'Example: Group-attendee trigger ignored first attendee because rule starts from attendee #2.',
        },
        {
            title: 'Delivery failure diagnostics',
            description:
                'Example: Webhook action failed with response code and retry metadata for debugging.',
        },
        {
            title: 'Action execution timeline',
            description:
                'Trace every action run for a meeting with timestamps and final status.',
        },
    ]

    const useCases: UseCase[] = [
        {
            title: 'Post-meeting review sequence',
            description:
                'After a session ends, send a thank-you email, push a webhook to your CRM, and queue the next follow-up.',
        },
        {
            title: 'No-show recovery loop',
            description:
                'When no-show state is available, trigger an SMS reminder, reschedule email, and recovery follow-up automatically.',
        },
        {
            title: 'Group session occupancy alerts',
            description:
                'From the second attendee onward, send alerts as workshops or cohort sessions fill up.',
        },
        {
            title: 'Paid booking follow-up',
            description:
                'When payment is received, trigger a welcome email and any onboarding or delivery steps tied to the offer.',
        },
        {
            title: 'Refund recovery workflow',
            description:
                'On refund, send a follow-up email with rebooking options and log the event for review.',
        },
        {
            title: 'First-booking activation',
            description:
                'When a client books a first session, send a welcome sequence and kick off the right onboarding flow.',
        },
        {
            title: 'Inactivity reactivation',
            description:
                'If last booking was X time ago, send timed reactivation nudges by email and WhatsApp.',
        },
        {
            title: 'Repeat-client appreciation flow',
            description:
                'When a client books repeatedly in a short period, send a loyalty offer or invite them into a longer-term package.',
        },
        {
            title: 'Reschedule continuity',
            description:
                'On attendee reschedule, refresh reminders and sync changes to downstream systems via webhook.',
        },
        {
            title: 'Multi-channel reminder stack',
            description:
                'Immediately after scheduling, create an email + SMS + WhatsApp reminder chain with merge-tag personalization.',
        },
        {
            title: 'Recurring meeting safeguards',
            description:
                'When one meeting in a recurring series is canceled, notify the client and apply the right follow-up policy.',
        },
        {
            title: 'Country-cost governance',
            description:
                'For international client bases, restrict SMS and WhatsApp to selected countries while routing other regions to email.',
        },
    ]
</script>
